import { useTranslation } from "@sagot/i18n";
import { AppLink, Avatar, Badge, Button, Checkbox, Icon } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { AskCount, ChannelCounts, ReasonBadge } from "./gap-bits";
import { phrasingPreview, reasonKey, type GapView } from "./gap-model";
import { GapRowMenu, type GapRowMenuProps } from "./gap-row-menu";

export const ROW_GRID = "grid grid-cols-[2rem_1fr] md:grid-cols-[2rem_1fr_9rem_8rem_15rem] items-start gap-x-4 gap-y-3 px-5 py-4";

export interface GapClusterRowProps extends Omit<GapRowMenuProps, "gap" | "canResolve"> {
  gap: GapView;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  canResolve: boolean;
  assigneeName: string | null;
  onWrite: () => void;
}

function Phrasings({ gap }: { gap: GapView }) {
  const { t } = useTranslation();
  const { visible, more } = phrasingPreview(gap);
  if (visible.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {visible.map((text) => (
        <li key={text} className="rounded-sm bg-sunken px-2 py-0.5 text-xs text-fg-muted">
          “{text}”
        </li>
      ))}
      {more > 0 ? <li className="px-2 py-0.5 text-xs font-semibold text-primary-ink">{t("engage.gaps.moreQuestions", { count: more })}</li> : null}
    </ul>
  );
}

function RowMeta({ gap }: { gap: GapView }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const quote = gap.feedback_quotes[0];
  const confidence = gap.avg_confidence === null ? null : t("engage.gaps.avgConfidence", { value: fmt.percent(gap.avg_confidence) });
  const lowConfidence = reasonKey(gap.reason) === "low_confidence";
  return (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-subtle">
        {gap.first_asked_at ? <span>{t("engage.gaps.firstLast", { first: fmt.date(gap.first_asked_at), last: fmt.ago(gap.last_asked_at) })}</span> : null}
        <ChannelCounts counts={gap.channel_counts} />
      </div>
      {lowConfidence && (confidence ?? gap.conflict_note) ? (
        <div className="mt-2 text-xs text-fg-subtle">{[confidence, gap.conflict_note].filter(Boolean).join(" · ")}</div>
      ) : null}
      {quote ? (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-fg-subtle">
          <Icon name="quote" className="mt-0.5 size-3" />
          <span>
            <span className="sr-only">{t("engage.gaps.feedbackQuote")}: </span>“{quote}”
          </span>
        </div>
      ) : null}
    </>
  );
}

/** Open cluster row (`GapClusterRow` in the mockup). */
export function GapClusterRow({ gap, selected, onSelectedChange, canResolve, assigneeName, onWrite, ...menu }: GapClusterRowProps) {
  const { t } = useTranslation();
  return (
    <li className={`${ROW_GRID} border-b border-line last:border-b-0 hover:bg-surface-2`} data-testid="gap-row">
      {canResolve ? (
        <Checkbox
          className="mt-1"
          checked={selected}
          onCheckedChange={(value) => {
            onSelectedChange(value === true);
          }}
          aria-label={t("engage.gaps.selectRow", { label: gap.label })}
        />
      ) : (
        <span />
      )}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-bold">{gap.label}</h3>
          {gap.category ? <Badge>{gap.category}</Badge> : null}
        </div>
        <Phrasings gap={gap} />
        <RowMeta gap={gap} />
      </div>
      <div className="col-start-2 md:col-start-auto">
        <AskCount count={gap.ask_count} trend={gap.trend} />
      </div>
      <div className="col-start-2 md:col-start-auto">
        <ReasonBadge reason={gap.reason} />
      </div>
      <div className="col-start-2 flex flex-wrap items-center gap-1.5 md:col-start-auto md:justify-end">
        {canResolve ? (
          <Button size="sm" onClick={onWrite}>
            <Icon name="pencil" />
            {t("engage.gaps.writeAnswer")}
          </Button>
        ) : null}
        <GapRowMenu gap={gap} canResolve={canResolve} {...menu} />
        {assigneeName ? (
          <span className="flex md:basis-full md:justify-end">
            <Badge title={t("engage.gaps.assignedTo", { name: assigneeName })}>
              <Avatar name={assigneeName} className="size-4 text-[8px]" />
              {assigneeName.split(" ").slice(0, 2).join(" ")}
            </Badge>
          </span>
        ) : null}
      </div>
    </li>
  );
}

/** Resolved cluster row with its Q&A source link. */
export function ResolvedGapRow({ gap, sourceHref }: { gap: GapView; sourceHref: string | null }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const date = fmt.date(gap.resolved_at);
  return (
    <li className={`${ROW_GRID} border-b border-line last:border-b-0`} data-testid="resolved-row">
      <Icon name="check-circle" className="mt-0.5 size-4 text-success" />
      <div className="min-w-0">
        <h3 className="font-bold">{gap.label}</h3>
        <div className="mt-1 text-xs text-fg-subtle">{gap.resolved_source_id ? t("engage.gaps.resolvedBy", { date }) : t("engage.gaps.resolvedNoSource", { date })}</div>
      </div>
      <div className="col-start-2 text-sm font-semibold tabular-nums md:col-start-auto">
        {gap.ask_count} {t("engage.gaps.asks", { count: gap.ask_count })}
      </div>
      <div className="col-start-2 md:col-start-auto">
        <Badge tone="success">
          <Icon name="check" className="size-3" />
          {t("engage.gaps.reason.resolved")}
        </Badge>
      </div>
      <div className="col-start-2 flex md:col-start-auto md:justify-end">
        {sourceHref ? (
          <Button asChild variant="ghost" size="sm">
            <AppLink href={sourceHref}>{t("engage.gaps.viewSource")}</AppLink>
          </Button>
        ) : null}
      </div>
    </li>
  );
}
